/**
 * A-5/B-3 자료함 미러링 — 채점 기준서·평가계획서를 저장할 때 자료함(참고자료)에도 텍스트 자료로 한 벌 보관한다.
 *
 * 원칙
 *  - 저장 흐름을 막지 않는다: 실패는 조용히 무시(호출 쪽은 결과만 보고 토스트 여부 결정).
 *  - 같은 제목 + 같은 글자수 자료가 이미 있으면 다시 만들지 않는다(같은 기준서로 여러 번 채점·저장하는 게 보통).
 *  - 무료 자료함은 텍스트 3개 한도라 조용히 채우면 안 된다 → PRO만 미러링(호출 쪽에서 isPro 확인).
 */

import { createTextReferenceMaterial, listReferenceMaterials } from '../api/referenceMaterialApi';

export const LIBRARY_TITLE_MAX = 60;

export const buildLibraryMirrorTitle = (prefix, body) => {
  const cleanBody = String(body || '').replace(/\s+/g, ' ').trim();
  const base = cleanBody ? `${prefix} · ${cleanBody}` : prefix;
  return base.length > LIBRARY_TITLE_MAX ? `${base.slice(0, LIBRARY_TITLE_MAX - 1)}…` : base;
};

const normalizeContent = (content) => String(content || '').replace(/\r\n/g, '\n').trim();

/**
 * @returns {Promise<{status: 'created'|'exists'|'skipped'|'failed', material?: object}>}
 */
export const mirrorTextToLibrary = async (currentUser, { title, content }) => {
  const normalized = normalizeContent(content);
  if (!currentUser || !normalized || normalized.length < 20) return { status: 'skipped' };
  const finalTitle = String(title || '').trim().slice(0, LIBRARY_TITLE_MAX);
  try {
    const listed = await listReferenceMaterials(currentUser);
    const materials = Array.isArray(listed?.materials) ? listed.materials : (Array.isArray(listed) ? listed : []);
    const duplicate = materials.find((m) => (
      String(m?.title || '').trim() === finalTitle && Number(m?.char_count || 0) === normalized.length
    ));
    if (duplicate) return { status: 'exists', material: duplicate };
    const created = await createTextReferenceMaterial(currentUser, { content: normalized, title: finalTitle });
    return { status: 'created', material: created?.material || created };
  } catch (error) {
    console.warn('자료함 미러링 실패(무시):', error?.message || error);
    return { status: 'failed' };
  }
};

const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : null);

/**
 * 파일로 올린 기준서는 rubric_text 가 비어 있다 → 서버가 파싱한 기준표(parsed_rubric)를 사람이 읽는 텍스트로 만든다.
 * 채점 화면 "직접 입력"에 다시 붙여도 그대로 쓸 수 있는 평문. 만들 게 없으면 ''.
 */
export const buildRubricTextFromParsed = (rubric) => {
  if (!rubric || typeof rubric !== 'object') return '';
  const lines = [];
  const title = [rubric.subject, rubric.name].filter(Boolean).join(' ');
  const total = num(rubric.total_points);
  if (title || total !== null) lines.push(`${title || '채점 기준서'}${total !== null ? ` (총 ${total}점)` : ''}`);
  if (rubric.task) lines.push(`과제: ${String(rubric.task).trim()}`);
  const standards = Array.isArray(rubric.achievement_standards) ? rubric.achievement_standards.filter(Boolean) : [];
  if (standards.length) lines.push(`성취기준: ${standards.join(' / ')}`);
  const base = num(rubric.base_points);
  if (base !== null && base > 0) lines.push(`기본 점수: ${base}점`);

  const items = Array.isArray(rubric.evaluation_items) ? rubric.evaluation_items : [];
  items.forEach((item, index) => {
    if (!item || typeof item !== 'object') return;
    const maxPoints = num(item.max_points);
    lines.push('');
    lines.push(`${index + 1}. ${item.name || `평가요소 ${index + 1}`}${maxPoints !== null ? ` (${maxPoints}점)` : ''}`);
    (Array.isArray(item.scoring_levels) ? item.scoring_levels : []).forEach((level) => {
      if (!level) return;
      if (typeof level === 'string') { lines.push(`- ${level}`); return; }
      const score = num(level.score);
      const head = `${level.level || ''}${score !== null ? `(${score})` : ''}`.trim();
      const desc = String(level.criteria || '').trim();
      lines.push(`- ${[head, desc].filter(Boolean).join(': ')}`);
    });
    (Array.isArray(item.minimum_conditions) ? item.minimum_conditions : []).forEach((rule) => {
      if (!rule?.condition) return;
      const score = num(rule.score);
      lines.push(`- 최저 조건: ${rule.condition}${score !== null ? ` → ${score}점` : ''}`);
    });
    (Array.isArray(item.deduction_rules) ? item.deduction_rules : []).forEach((rule) => {
      if (!rule?.condition) return;
      const points = num(rule.points);
      lines.push(`- 감점: ${rule.condition}${points !== null ? ` -${points}점` : ''}`);
    });
    (Array.isArray(item.bonus_rules) ? item.bonus_rules : []).forEach((rule) => {
      if (!rule?.condition) return;
      const points = num(rule.points);
      lines.push(`- 가산: ${rule.condition}${points !== null ? ` +${points}점` : ''}`);
    });
    (Array.isArray(item.checklist) ? item.checklist : []).forEach((check) => {
      const text = typeof check === 'string' ? check : (check?.name || check?.condition || '');
      if (text) lines.push(`- 체크: ${text}`);
    });
  });
  return lines.join('\n').trim();
};
